import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Link } from '@lifeomic/chroma-react/components/Link';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import { Info } from '@lifeomic/chromicons';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import KnowledgeService from '@lifeomic/phc-web-toolkit/src/services/KnowledgeService';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import urls from '../../../../src/util/urls';
import EmptyPageState from '@lifeomic/phc-web-toolkit/src/components/EmptyPageState';
import { DNAIcon } from '../../../../src/components/common/icons';
import { DetailColumn } from './DetailColumn';
import DiseaseTable from './DiseaseTable';
import { BarChart } from './Visualizations/BarChart';
import { Box } from '@lifeomic/chroma-react/components/Box';

const knowledgeService = new KnowledgeService();

const messages = defineMessages({
  gene: {
    id: 'omicsExplorer.variantDetails.dialog.gene',
    defaultMessage: 'Gene',
  },
  name: {
    id: 'omicsExplorer.variantDetails.dialog.name',
    defaultMessage: 'Name',
  },
  alias: {
    id: 'omicsExplorer.variantDetails.dialog.alias',
    defaultMessage: 'Aliases',
  },
  summary: {
    id: 'omicsExplorer.variantDetails.dialog.summary',
    defaultMessage: 'Summary',
  },
  cytoband: {
    id: 'omicsExplorer.variantDetails.dialog.cytoband',
    defaultMessage: 'Cytoband',
  },
  noGenes: {
    id: 'omicsExplorer.variantDetails.dialog.noGenes',
    defaultMessage: 'No gene details found',
  },
});

const getVariantGene = variant =>
  (variant.ensemblCanon?.length > 0 && variant.ensemblCanon[0].gene) ||
  (variant.ensembl?.length > 0 && variant.ensembl[0].gene);

const columns = formatMessage => [
  {
    header: {
      label: formatMessage(messages.gene),
    },
    cell: {
      content: value => (
        <Link
          to={urls.ncbi.gene(value.gene)}
          onClick={e => e.stopPropagation()}
          newTab
        >
          {value.gene}
        </Link>
      ),
    },
  },
  {
    header: {
      label: formatMessage(messages.name),
    },
    cell: {
      valuePath: 'name',
    },
  },
  {
    header: {
      label: formatMessage(messages.alias),
    },
    cell: {
      content: value => value.alias?.join(', '),
    },
  },
  {
    header: {
      label: formatMessage(messages.cytoband),
    },
    cell: {
      valuePath: 'cytoband',
    },
  },
  {
    header: {
      label: formatMessage(messages.summary),
    },
    cell: {
      valuePath: 'summary',
    },
  },
];

export const GeneDetails = ({
  data,
  contentClassName,
  emptyStateClassName,
}) => {
  const { formatMessage } = useIntl();
  const [getGenes, getGenesState] = useService(knowledgeService.getGenes);
  const [genes, setGenes] = React.useState([]);

  React.useEffect(() => {
    const geneIds = [
      getVariantGene(data),
      data.gene,
      data.geneId,
      data.geneId1,
      data.geneId2,
    ].filter(g => !!g);

    if (geneIds.length > 0) {
      getGenes({ gene: [...new Set(geneIds)].join(',') });
    }
  }, [data, getGenes]);

  React.useEffect(() => {
    if (getGenesState?.response?.data?.items) {
      setGenes(getGenesState?.response?.data?.items?.filter(g => g.name));
    }
  }, [getGenesState]);

  if (!!getGenesState.lastFetchAttempt && genes.length === 0) {
    return (
      <div className={emptyStateClassName}>
        <EmptyPageState
          message={formatMessage(messages.noGenes)}
          illustration={DNAIcon}
        />
      </div>
    );
  }

  if (getGenesState.isFetching) {
    return (
      <Box align="center" fullHeight justify="center">
        <DotLoader />
      </Box>
    );
  }

  return (
    <div className={contentClassName}>
      {genes.map((gene, key) => (
        <DetailColumn key={key} columns={columns(formatMessage)} data={gene} />
      ))}
    </div>
  );
};
